import { observer } from 'mobx-react-lite'
import { lazy, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeftOutlined } from '@ant-design/icons'

import { useStore } from 'store/useStore'
import { ContainerLK } from 'sections/LKSection/shared/components/ContainerLK/ContainerLK'
import { Margin, Padding } from 'components/ui'
import { Button, Flex, Skeleton, Title } from 'components/uiKit'
import { useAsyncEffect } from 'utils/hooks/useAsyncEffect'
import { LKRoutesEnum } from 'shared/constants/routes'
import { SEARCH_PARAMS } from 'shared/constants/values'

import { TicketKeys } from '../../data'
import { TicketActions } from './Actions/TicketActions'
import { TicketChat } from './Chat/TicketChat'
import { TicketInfo } from './Info/TicketInfo'

const getKey = (key: string) => `${TicketKeys.ticketItemPage}.${key}`

const TicketItemPage = observer(() => {
  const { viewTicketUiStore: uiStore } = useStore()

  const { ticketId } = useParams()
  const navigate = useNavigate()
  const { t } = useTranslation()

  const ticket = uiStore.selectedTicket

  const searchParams = new URLSearchParams(location.search)
  const pageFromUrl = searchParams.get(SEARCH_PARAMS.page)
  const pageSizeFromUrl = searchParams.get(SEARCH_PARAMS.pageSize)

  useEffect(() => {
    if (pageFromUrl && pageSizeFromUrl) {
      uiStore.setPage(+pageFromUrl)
      uiStore.setPageSize(+pageSizeFromUrl)
    }
  }, [pageFromUrl, pageSizeFromUrl, uiStore])

  useAsyncEffect(async () => {
    if (!ticket && ticketId) {
      await uiStore.getTicketList()
      uiStore.setSelectedTicket(uiStore.getTicket(+ticketId))
    }
  }, [ticketId])

  const backButtonHandler = () => {
    navigate(LKRoutesEnum.tickets)
  }

  return (
    <ContainerLK>
      <Padding py={32}>
        <Flex vertical style={{ maxWidth: 700 }}>
          <Margin mb={32}>
            <Flex gap={16} align="center">
              <Button shape="circle" icon={<ArrowLeftOutlined />} onClick={backButtonHandler} />

              <Title>
                {t(getKey('Тикет'))} № {isNaN(Number(ticketId)) ? '–' : ticketId}
              </Title>
            </Flex>
          </Margin>

          <Skeleton loading={uiStore.ticketStore.loading}>
            {ticket ? (
              <Flex vertical gap={32}>
                <TicketInfo ticket={ticket} />
                <TicketActions status={ticket.status} canRate={ticket.canRate} />
                <TicketChat supportLogs={ticket.supportLogs} status={ticket.status} />
              </Flex>
            ) : (
              <Flex>
                <Title type="secondary" level={4}>
                  {t(getKey('Информации по данному тикету нет'))}
                </Title>
              </Flex>
            )}
          </Skeleton>
        </Flex>
      </Padding>
    </ContainerLK>
  )
})

export default TicketItemPage

export const TicketItemPageAsync = lazy(() => import('./TicketItemPage'))
